import type { AuthUser, LicenseContext } from '@chefiapro/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { authService, type LoginCredentials } from '@/services/auth.service';
import { ApiError } from '@/api/api-client';
import { setAccessToken } from '@/api/api-client';
import type { Session } from '@/services/auth.service';

interface AuthContextValue {
  user: AuthUser | null;
  license: LicenseContext | null;
  status: 'loading' | 'authenticated' | 'anonymous' | 'error';
  retrySession: () => Promise<void>;
  login: (credentials: LoginCredentials) => Promise<void>;
  acceptSession: (session: Session) => void;
  impersonate: (code: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(null);
  const authenticatedUser = useRef<AuthUser | null>(null);
  authenticatedUser.current = user;
  const [license, setLicense] = useState<LicenseContext | null>(null);
  const [status, setStatus] = useState<AuthContextValue['status']>('loading');

  useEffect(() => {
    const onEnded = () => {
      if (authenticatedUser.current) queryClient.clear();
      setUser(null);
      setLicense(null);
      setStatus('anonymous');
    };
    window.addEventListener('chefiapro-session-ended', onEnded);
    return () => window.removeEventListener('chefiapro-session-ended', onEnded);
  }, [queryClient]);
  useEffect(() => {
    let active = true;
    authService
      .refresh()
      .then((session) => {
        if (!active) return;
        setUser(session.user);
        setLicense(session.license);
        setStatus('authenticated');
      })
      .catch((error: unknown) => {
        if (!active) return;
        setStatus(
          error instanceof ApiError && [401, 403].includes(error.status) ? 'anonymous' : 'error',
        );
      });
    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      const session = await authService.login(credentials);
      queryClient.clear(); // Do not show a previous tenant's cached data after a new login.
      setUser(session.user);
      setLicense(session.license);
      setStatus('authenticated');
    },
    [queryClient],
  );

  const acceptSession = useCallback(
    (session: Session) => {
      setAccessToken(session.accessToken);
      queryClient.clear();
      setUser(session.user);
      setLicense(session.license);
      setStatus('authenticated');
    },
    [queryClient],
  );

  const impersonate = useCallback(
    async (code: string) => {
      const session = await authService.impersonate(code);
      queryClient.clear();
      setUser(session.user);
      setLicense(session.license);
      setStatus('authenticated');
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
    setLicense(null);
    setStatus('anonymous');
  }, []);
  const retrySession = useCallback(async () => {
    setStatus('loading');
    try {
      const session = await authService.refresh();
      setUser(session.user);
      setLicense(session.license);
      setStatus('authenticated');
    } catch (error) {
      setStatus(
        error instanceof ApiError && [401, 403].includes(error.status) ? 'anonymous' : 'error',
      );
    }
  }, []);

  const value = useMemo(
    () => ({ user, license, status, login, acceptSession, impersonate, logout, retrySession }),
    [impersonate, license, login, acceptSession, logout, status, user, retrySession],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth deve ser usado dentro de AuthProvider.');
  return context;
}
